import React, { useMemo, useState } from "react";
import { Box, CheckboxCard, CheckboxGroup, HStack, Icon, SimpleGrid, Stack, Switch, Text } from "@chakra-ui/react";
import { DashboardCheckboxCardControl, DashboardInput } from "components/utils/FormControls";
import { MdDirectionsBusFilled, MdHome, MdPayments } from "react-icons/md";
import { FaTools } from "react-icons/fa";
import { HiCheck, HiX } from "react-icons/hi";
import { DataCard } from "components/utils/DataCard";
import { FormDialog } from "components/utils/FormDialog";
import { useDialogForm } from "utils/useDialogForm";

function formatCurrencyMXN(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "-";
  try {
    return Number(value).toLocaleString("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 0 });
  } catch {
    return `$${value}`;
  }
}

const RESOURCE_OPTIONS = [
  {
    id: "van",
    label: "Van",
    description: "Transporte del club",
    icon: <MdDirectionsBusFilled />,
    solid: "#0f766e",
  },
  {
    id: "club",
    label: "Club",
    description: "Casa club / sede",
    icon: <MdHome />,
    solid: "#2563eb",
  },
];

function resourceTint(color, amount = 12) {
  return `color-mix(in srgb, ${color} ${amount}%, transparent)`;
}

export const PrevioLogisticaCard = ({ actividad, actividadId, patch }) => {
  const dialog = useDialogForm({ presupuesto: "", recursosRequeridos: true, recursos: [] });
  const [localError, setLocalError] = useState(null);

  const saving = patch.isPending;
  const patchError = patch.isError ? String(patch.error?.message || patch.error) : null;

  const presupuestoTxt = useMemo(() => formatCurrencyMXN(actividad?.presupuesto), [actividad?.presupuesto]);
  const recursosRequeridos = actividad?.recursosRequeridos !== false;
  const selectedResources = useMemo(
    () => RESOURCE_OPTIONS.filter((option) => (option.id === "van" ? actividad?.van : actividad?.club)),
    [actividad?.club, actividad?.van]
  );

  function openDialog() {
    setLocalError(null);
    dialog.openWith({
      presupuesto: actividad?.presupuesto === null || actividad?.presupuesto === undefined ? "" : String(actividad.presupuesto),
      recursosRequeridos,
      recursos: [
        ...(actividad?.van ? ["van"] : []),
        ...(actividad?.club ? ["club"] : []),
      ],
    });
  }

  function setRecursosRequeridos(value) {
    dialog.setField("recursosRequeridos", value);
    if (!value) dialog.setField("recursos", []);
  }

  async function save() {
    setLocalError(null);

    const raw = String(dialog.form.presupuesto ?? "").trim();
    const n = raw === "" ? null : Number(raw);
    if (n !== null && Number.isNaN(n)) {
      setLocalError("Presupuesto invalido");
      return;
    }

    const needsRecursos = dialog.form.recursosRequeridos !== false;
    const recursos = needsRecursos && Array.isArray(dialog.form.recursos) ? dialog.form.recursos : [];
    await patch.mutateAsync({
      actividadId,
      patch: {
        presupuesto: n,
        recursosRequeridos: needsRecursos,
        van: recursos.includes("van"),
        club: recursos.includes("club"),
      },
    });
    dialog.close();
  }

  return (
    <>
      <DataCard
        title="Logistica"
        variant="teal"
        headerIcon={<FaTools size="24px" />}
        onEdit={openDialog}
        editAriaLabel="Editar logistica"
        isIncomplete={(actividad?.presupuesto === null || actividad?.presupuesto === undefined) || (recursosRequeridos && selectedResources.length === 0)}
      >
        <Stack gap="4" alignItems="start">
          <HStack gap="2.5" align="start">
            <Box color="dashboard.fg.muted" mt="0.5" flexShrink={0}><MdPayments size="16px" /></Box>
            <Stack gap="0">
              <Text fontSize="xs" color="dashboard.fg.muted" textTransform="uppercase" fontWeight="700" letterSpacing="0.06em">Presupuesto</Text>
              <Text fontWeight="600" fontSize="sm" color="dashboard.fg.default">{presupuestoTxt}</Text>
            </Stack>
          </HStack>

          <Box w="full">
            <Text fontSize="xs" color="dashboard.fg.muted" textTransform="uppercase" fontWeight="700" letterSpacing="0.06em" mb="2">
              Recursos
            </Text>
            {!recursosRequeridos ? (
              <Text fontWeight="700" color="dashboard.fg.default" fontSize="sm">No se requieren recursos.</Text>
            ) : selectedResources.length === 0 ? (
              <Text color="dashboard.fg.muted" fontSize="sm">Recursos sin definir.</Text>
            ) : (
              <SimpleGrid minChildWidth="120px" gap="2.5">
                {selectedResources.map((option) => (
                  <HStack
                    key={option.id}
                    gap="2.5"
                    p="3"
                    rounded="2xl"
                    bg={resourceTint(option.solid, 12)}
                  >
                    <Box bg={option.solid} boxSize="9" display="grid" placeItems="center" rounded="full" flexShrink={0}>
                      <Icon fontSize="22px" color="white">
                        {option.icon}
                      </Icon>
                    </Box>
                    <Text fontSize="sm" fontWeight="800" color="dashboard.fg.default">{option.label}</Text>
                  </HStack>
                ))}
              </SimpleGrid>
            )}
          </Box>
        </Stack>
      </DataCard>

      <FormDialog open={dialog.open} onOpenChange={dialog.onOpenChange} title="Editar Logistica" errorText={localError || patchError} saving={saving} onCancel={dialog.close} onSave={save}>
        <Box>
          <Text fontSize="sm" fontWeight="600" color="dashboard.fg.default" mb="1">Presupuesto</Text>
          <DashboardInput inputMode="decimal" placeholder="Ej. 500" value={dialog.form.presupuesto} onChange={(e) => dialog.setField("presupuesto", e.target.value)} />
        </Box>

        <Box>
          <HStack justify="space-between" align="center" mb="3">
            <Box>
              <Text fontSize="sm" fontWeight="600" color="dashboard.fg.default">Recursos</Text>
              <Text color="dashboard.fg.muted" fontSize="sm">Se necesitan recursos para esta actividad?</Text>
            </Box>
            <Switch.Root size="lg" checked={dialog.form.recursosRequeridos !== false} onCheckedChange={(e) => setRecursosRequeridos(!!e.checked)}>
              <Switch.HiddenInput />
              <Switch.Control>
                <Switch.Thumb>
                  <Switch.ThumbIndicator fallback={<HiX color="black" />}>
                    <HiCheck />
                  </Switch.ThumbIndicator>
                </Switch.Thumb>
              </Switch.Control>
            </Switch.Root>
          </HStack>

          {dialog.form.recursosRequeridos !== false && (
            <CheckboxGroup
              name="recursosLogistica"
              value={dialog.form.recursos}
              onValueChange={(v) => {
                const next = Array.isArray(v) ? v : v?.value;
                dialog.setField("recursos", next ?? []);
              }}
            >
              <SimpleGrid minChildWidth="150px" gap="2">
                {RESOURCE_OPTIONS.map((option) => (
                  <CheckboxCard.Root unstyled align="center" key={option.id} value={option.id}>
                    <CheckboxCard.HiddenInput />
                    <DashboardCheckboxCardControl accentColor={option.solid} selectedVariant="outlined" selectedBg={resourceTint(option.solid, 12)}>
                      <CheckboxCard.Content display="flex" flexDirection="column" alignItems="center" textAlign="center" w="full">
                        <Box bg={option.solid} boxSize="11" display="grid" placeItems="center" rounded="full" mb="3" mx="auto">
                          <Icon fontSize="2xl" color="white">
                            {option.icon}
                          </Icon>
                        </Box>
                        <CheckboxCard.Label fontWeight="800">{option.label}</CheckboxCard.Label>
                        <CheckboxCard.Description color="dashboard.fg.muted">{option.description}</CheckboxCard.Description>
                      </CheckboxCard.Content>
                    </DashboardCheckboxCardControl>
                  </CheckboxCard.Root>
                ))}
              </SimpleGrid>
            </CheckboxGroup>
          )}
        </Box>
      </FormDialog>
    </>
  );
};
